import { useSelector } from '@/store'
import type { GoodFieldConfigType, GoodFieldNameType } from '@/store/commonSlice/types'
import { useMemo, useCallback } from 'react'

type IParams = {
  warehouseId: string
}

/** 获取商品信息字段配置 */
export function useGoodsField(params?: IParams) {
  const { warehouseId } = params || {}
  const warehouseInfoList = useSelector((state) => state.common?.warehouseInfoList)
  const currentWarehouse = warehouseInfoList?.find((item) => item.warehouseId === warehouseId)

  // 有一个仓库展示，则展示
  const getFieldVisible = useCallback(
    (field: GoodFieldNameType) => {
      return warehouseInfoList
        .map((item) => item.providerFieldConfigs.find((item) => item.fieldName === field))
        .filter(Boolean)
        ?.some((item) => !!item?.fieldConfig?.createSkuShow)
    },
    [warehouseInfoList]
  )

  const getFieldByWarehouseId = useCallback(
    (field: GoodFieldNameType, configName: keyof GoodFieldConfigType = 'createInboundRequire') => {
      return !!currentWarehouse?.providerFieldConfigs?.find((item) => item.fieldName === field)?.fieldConfig?.[configName]
    },
    [currentWarehouse?.providerFieldConfigs]
  )

  // 有一个仓库不允许重复，就不允许重复
  const getFieldSupportRepeat = useCallback(
    (field: GoodFieldNameType) => {
      return warehouseInfoList
        .map((item) => item.providerFieldConfigs.find((item) => item.fieldName === field))
        .filter(Boolean)
        ?.every((item) => !!item?.fieldConfig?.supportRepeat)
    },
    [warehouseInfoList]
  )

  const fieldConfig = useMemo(
    () => ({
      skuEnNameVisible: getFieldVisible('skuEnName'),
      skuZhNameVisible: getFieldVisible('skuZhName'),
      skuTitleVisible: getFieldVisible('skuTitle'),
      weightVisible: getFieldVisible('weight'),
      volumeVisible: getFieldVisible('volume'),

      hscodeVisible: getFieldVisible('hscode'),
      originCountryVisible: getFieldVisible('originCountry'),
      barcodeSupportRepeat: getFieldSupportRepeat('barcode'),
      warehouseFieldConfig: {
        skuEnNameRequired: getFieldByWarehouseId('skuEnName'),
        skuZhNameRequired: getFieldByWarehouseId('skuZhName'),
        skuTitleRequired: getFieldByWarehouseId('skuTitle'),
        barcodeRequire: getFieldByWarehouseId('barcode'),
        barcodeSupportRepeat: getFieldByWarehouseId('barcode', 'supportRepeat'),
      },
    }),
    [getFieldByWarehouseId, getFieldVisible, getFieldSupportRepeat]
  )

  return fieldConfig
}
